import { strFromU8, strToU8, unzipSync, zipSync, type Zippable } from "fflate";
import { parseCapsuleFile, type CapsuleFile } from "./capsuleFile.js";
import { blobHash, cleanFileName } from "./manifest.js";

/**
 * Download archive (PLAN.md 4.8, 4.11 H10): capsules, their encrypted blobs, the offline opener
 * and a README in one .zip. Every path inside is generated here, never taken from a manifest:
 *
 *   <folder>/README.txt
 *   <folder>/vrelis-opener.html
 *   <folder>/capsules/<event id>.json        capsule file (vrelis-capsule-v1)
 *   <folder>/blobs/<sha256>.bin              encrypted attachment, as stored on the server
 *   <folder>/opened/<event id>/<clean name>  decrypted attachment, only if the member chose so
 */
export type ArchiveCapsule = {
  file: CapsuleFile;
  /** Decrypted attachments, only when the member chose to include opened content. */
  openedFiles?: { name: string; bytes: Uint8Array }[];
};

export type ArchiveInput = {
  folder: string;
  capsules: ArchiveCapsule[];
  /** Encrypted blobs by sha256. Each is checked against its address before it goes in. */
  blobs: Map<string, Uint8Array>;
  openerHtml: string | null;
  readme: string;
};

export class ArchiveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ArchiveError";
  }
}

const HEX64 = /^[0-9a-f]{64}$/;

/** A folder or file name we generate: only safe characters. */
const safeSegment = (s: string) => s.replace(/[^A-Za-z0-9._-]/g, "_").replace(/^\.+/, "") || "vrelis";

export function buildArchive(input: ArchiveInput): Uint8Array {
  const root = safeSegment(input.folder);
  const files: Zippable = {};
  const put = (path: string, data: Uint8Array) => {
    files[`${root}/${path}`] = [data, { level: 0 }];
  };
  put("README.txt", strToU8(input.readme));
  if (input.openerHtml) put("vrelis-opener.html", strToU8(input.openerHtml));
  for (const c of input.capsules) {
    const id = c.file.event.id;
    if (!HEX64.test(id)) throw new ArchiveError("capsule id is not a 64-character hex id");
    put(`capsules/${id}.json`, strToU8(JSON.stringify(c.file, null, 2)));
    const taken = new Set<string>();
    for (const f of c.openedFiles ?? []) {
      let name = cleanFileName(f.name);
      for (let n = 2; taken.has(name.toLowerCase()); n++) {
        const dot = name.lastIndexOf(".");
        name = dot > 0 ? `${name.slice(0, dot)} (${n})${name.slice(dot)}` : `${name} (${n})`;
      }
      taken.add(name.toLowerCase());
      put(`opened/${id}/${name}`, f.bytes);
    }
  }
  for (const [sha, bytes] of input.blobs) {
    if (!HEX64.test(sha) || blobHash(bytes) !== sha) throw new ArchiveError(`blob ${sha.slice(0, 8)}… does not match its address`);
    put(`blobs/${sha}.bin`, bytes);
  }
  // Stored, not compressed: blobs and images are random-looking; text is small.
  return zipSync(files, { level: 0 });
}

/**
 * Limits for reading an archive (PLAN.md 4.13 R9), checked on the sizes the zip declares before
 * anything is decompressed, so a crafted archive cannot fill memory or hang the page:
 * - one blob entry at most 64 MiB, one capsule .json at most 1 MiB (an event is at most 32 KiB;
 *   the file adds a beacon and, if chosen, the opened text and file list);
 * - at most 5000 capsule and blob entries, together at most 512 MiB.
 */
export const MAX_ARCHIVE_ENTRY_BYTES = 64 * 1024 * 1024;
export const MAX_ARCHIVE_CAPSULE_BYTES = 1024 * 1024;
export const MAX_ARCHIVE_ENTRIES = 5000;
export const MAX_ARCHIVE_TOTAL_BYTES = 512 * 1024 * 1024;

export type ArchiveLimits = { entryBytes: number; capsuleBytes: number; entries: number; totalBytes: number };
export const ARCHIVE_LIMITS: ArchiveLimits = {
  entryBytes: MAX_ARCHIVE_ENTRY_BYTES,
  capsuleBytes: MAX_ARCHIVE_CAPSULE_BYTES,
  entries: MAX_ARCHIVE_ENTRIES,
  totalBytes: MAX_ARCHIVE_TOTAL_BYTES,
};

const mib = (n: number) => (n >= 1024 * 1024 ? `${n / 1024 / 1024} MiB` : `${n} bytes`);

const CAPSULE_PATH = /^(?:[^/]+\/)?capsules\/([0-9a-f]{64})\.json$/;
const BLOB_PATH = /^(?:[^/]+\/)?blobs\/([0-9a-f]{64})\.bin$/;

/**
 * Reads an archive in memory (the opener writes nothing to disk). Only capsules/*.json and
 * blobs/*.bin are read; anything else in the zip is ignored. A blob whose bytes do not hash to
 * its name is dropped. The whole archive is refused if it breaks the limits above; a single
 * entry over its own limit is skipped with a note.
 */
export function readArchive(zip: Uint8Array, limits: ArchiveLimits = ARCHIVE_LIMITS): { capsules: CapsuleFile[]; blobs: Map<string, Uint8Array>; problems: string[] } {
  const problems: string[] = [];
  const short = (name: string) => name.split("/").pop()!.slice(0, 12);
  // First pass: the directory only (the filter returns false, nothing is inflated).
  let count = 0;
  let total = 0;
  try {
    unzipSync(zip, {
      filter: (f) => {
        const capsule = CAPSULE_PATH.test(f.name);
        if (!capsule && !BLOB_PATH.test(f.name)) return false;
        const limit = capsule ? limits.capsuleBytes : limits.entryBytes;
        if (f.originalSize > limit) problems.push(`${short(f.name)}… is larger than ${mib(limit)} and was skipped`);
        else {
          count++;
          total += f.originalSize;
        }
        return false;
      },
    });
  } catch (err) {
    throw new ArchiveError(`not a readable .zip archive: ${err instanceof Error ? err.message : String(err)}`);
  }
  if (count > limits.entries) throw new ArchiveError(`the archive has ${count} capsules and files; at most ${limits.entries} are read`);
  if (total > limits.totalBytes) throw new ArchiveError(`the archive unpacks to more than ${mib(limits.totalBytes)}`);

  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(zip, {
      filter: (f) =>
        (CAPSULE_PATH.test(f.name) && f.originalSize <= limits.capsuleBytes) || (BLOB_PATH.test(f.name) && f.originalSize <= limits.entryBytes),
    });
  } catch (err) {
    throw new ArchiveError(`not a readable .zip archive: ${err instanceof Error ? err.message : String(err)}`);
  }
  const capsules: CapsuleFile[] = [];
  const blobs = new Map<string, Uint8Array>();
  for (const [path, data] of Object.entries(entries)) {
    // The declared size is the sender's claim; the unpacked size is checked again.
    const capsuleEntry = CAPSULE_PATH.test(path);
    if (data.length > (capsuleEntry ? limits.capsuleBytes : limits.entryBytes)) {
      problems.push(`${short(path)}… unpacked larger than declared and was skipped`);
      continue;
    }
    const blob = BLOB_PATH.exec(path);
    if (blob) {
      if (blobHash(data) === blob[1]) blobs.set(blob[1], data);
      else problems.push(`${blob[1].slice(0, 8)}….bin does not match its name and was skipped`);
      continue;
    }
    try {
      capsules.push(parseCapsuleFile(strFromU8(data)));
    } catch (err) {
      problems.push(`${CAPSULE_PATH.exec(path)?.[1].slice(0, 8)}….json: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
  return { capsules, blobs, problems };
}
